import './property';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import '@lit-labs/virtualizer';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-sidebar' )
export class AutomatorLogSidebar extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties

	// The title displayed on the header, next to the "X" that closes the sidebar
	@property( { type: String, attribute: false } ) accessor title = escapeHTML( __( 'Details', 'uncanny-automator' ) );

	// The summary of the data that's being displayed
	// {
	//     "title": String or TemplateResult,
	//     "items": [
	//         {
	//             "label": String,
	//             "value": String or TemplateResult,
	//         },
	//     ]
	// }
	@property( { type: Object, attribute: false } ) accessor summary;

	// Data about each run/try/number of times
	// {
	//     title: 'Try 2/2',
	//     summary: [
	//         {
	//             label: 'Status',
	//             value: String/TemplateResult
	//         }
	//     ]
	//     properties: [
	//         {
	//             type: 'url',
	//             label: 'URL',
	//             value: 'https://domain.com/wp-json/uap/v2/uap-10586-10588'
	//         },
	//         {
	//             type: 'email',
	//             label: 'Email address',
	//             value: { raw: '{{user_email}}', parsed: 'agustin@uncannyowl.com' }
	//         },
	//     ]
	// },
	// {
	//     title: 'Try 1/2',
	//     properties: [
	//         ...
	//     ]
	// }
	@property( { type: Array, attribute: false } ) accessor runs = [];

	// Miscellaneous data. This is used, for example, to set `.runsAlwaysShowSummaries`
	@property( { type: Object, attribute: false } ) accessor miscellaneous = {};

	// Whether a "load more" request is in flight
	@property( { type: Boolean, attribute: false } ) accessor _isLoadingMore = false;

	// Safety timeout ID to reset loading state if the response never arrives
	_loadingTimeout = null;

	// Cached reversed runs array — stable reference between renders so the
	// virtualizer keeps its internal layout state (cached heights, scroll position).
	_reversedRuns = [];

	// eslint-disable-next-line require-jsdoc
	willUpdate( changedProperties ) {
		// Reset loading state when runs are updated (new page arrived).
		if ( changedProperties.has( 'runs' ) || changedProperties.has( 'miscellaneous' ) ) {
			this._isLoadingMore = false;
			clearTimeout( this._loadingTimeout );
		}

		// Recompute the reversed array only when runs actually change.
		if ( changedProperties.has( 'runs' ) ) {
			this._reversedRuns = [ ...this.runs ].reverse();
		}
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		// Get data or show placeholder
		return html`
			<div class="sidebar">
				${ this._templateHeader }

				<div class="sidebar-content">
					${ this._templateSummary }
					${ this._templateRuns }
				</div>

				${ this._templateRunsPagination }
			</div>
		`;
	}

	/**
	 * Request the parent to close the sidebar and remove the existing information
	 */
	requestCloseSidebar() {
		// Close it
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-sidebar', {
				bubbles: true,
				composed: true,
				detail: {
					action: 'close',
				}
			} )
		);

		return;
	}

	/**
	 * Requests the dialog to close itself
	 */
	requestCloseDialog() {
		this.dispatchEvent(
			new CustomEvent(
				'uap-log-request-dialog-close',
				{
					bubbles: true,
					composed: true
				}
			)
		);

		return;
	}

	/**
	 * Returns the template of the header of the sidebar
	 *
	 * @return {TemplateResult} The template of the header
	 */
	get _templateHeader() {
		return html`
			<div class="sidebar-header">
				<uo-tooltip
					class="sidebar-header-close-sidebar"
				>
					${ escapeHTML( __( 'Close sidebar', 'uncanny-automator' ) ) }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"
						@click=${ this.requestCloseSidebar }
					>
						<uo-icon id="angle-left"></uo-icon>
					</uo-button>
				</uo-tooltip>

				<div class="sidebar-header-title">
					${ this.title }
				</div>

				<uo-tooltip
					class="sidebar-header-close-dialog"
				>
					${ escapeHTML( __( 'Close log details', 'uncanny-automator' ) ) }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"
						@click=${ this.requestCloseDialog }
					>
						<uo-icon id="xmark"></uo-icon>
					</uo-button>
				</uo-tooltip>
				
			</div>
		`;
	}

	/**
	 * Returns the template of the summary of the sidebar
	 * This includes a title that summarizes the content below and a table with miscellaneous data.
	 *
	 * @return {TemplateResult} The template of the summary
	 */
	get _templateSummary() {
		return html`
			<div class="sidebar-summary">
				${
					! isEmpty( this.summary.title ) ?
					html`
						<div class="sidebar-summary-title">
							${ this.summary.title }
						</div>
					` :
					''
				}

				${
					! isEmpty( this.summary.items ) ?
					html`
						<table class="sidebar-summary-items">
							<tbody>
								${
									repeat(
										this.summary.items,
										( item, index ) => index,
										item => {
											// Allow markdown
											const itemValue =
												typeof item.value === 'string' ?
												html`
													<uo-markdown 
														allowed-tags="strong,em,ul,ol,li,a"
														.content=${ item.value }>
													</uo-markdown>
												` :
												item.value;

											return html`
												<tr>
													<td class="item__key">
														${ item.label }
													</td>
													<td class="item__value">
														${ itemValue }
													</td>
												</tr>
											`;
										}
									)
								}
							</tbody>
						</table>
					`:
					''
				}
			</div>
		`;
	}

	/**
	 * Renders the trigger/action/condition runs/number of times/tries/instances
	 *
	 * @return {TemplateResult} The template of the runs
	 */
	get _templateRuns() {
		// Check if there are runs
		if ( this.runs.length === 0 ) {
			return [];
		}

		// Check if there is only one run and if the dev doesn't want to show the summary anyway
		if ( this.runs.length === 1 && ! this.runsAlwaysShowSummaries ) {
			return this._templateRunsOneRun;
		}

		return this._templateRunsMultipleRuns;
	}

	/**
	 * The template used when there is one run
	 *
	 * @return {TemplateResult} The template of the runs when there is only one
	 */
	get _templateRunsOneRun() {
		// Get the data of that single run
		const run = this.runs[0];

		return html`
			<div class="sidebar-runs">
				<div class="sidebar-runs__run">
					${
						! isEmpty( run.properties ) ?
						html `
							<div class="sidebar-runs__run-properties">
								${
									repeat(
										run.properties,
										( property, index ) => index,
										property => html`
											<uap-log-sidebar-property
												.type="${ property.type }"
												.label="${ property.label }"
												.value="${ property.value }"
												.attributes="${ property.attributes }"
											></uap-log-sidebar-property>
										`
									)
								}
							</div>
						` :
						''
					}
				</div>
			</div>
		`;
	}

	/**
	 * The template used when there are multiple runs
	 *
	 * @return {TemplateResult} The template of the runs when there are multiple
	 */
	get _templateRunsMultipleRuns() {
		return html`
			<div class="sidebar-runs">
				<lit-virtualizer
					.items=${ this._reversedRuns }
					.renderItem=${ ( run, index ) => this.templateRunItem( run, index ) }
				></lit-virtualizer>
			</div>
		`;
	}

	/**
	 * Shows page navigation when runs are paginated.
	 *
	 * @return {TemplateResult|string}
	 */
	get _templateRunsPagination() {
		const pagination = this.miscellaneous?.pagination;

		if ( ! pagination ) {
			return '';
		}

		return html`
			<div class="sidebar-runs__pagination">
				<uo-button
					size="small"
					color="secondary"
					?disabled=${ ! pagination.hasPrev || this._isLoadingMore }
					@click=${ this._handlePrevPage }
				>
					&lsaquo;
				</uo-button>

				<span class="sidebar-runs__pagination-label">
					${ this._isLoadingMore
						? escapeHTML( __( 'Loading…', 'uncanny-automator' ) )
						: sprintf(
							/* translators: 1. Current page, 2. Total pages, 3. Total items */
							escapeHTML( __( 'Page %1$s of %2$s (%3$s items)', 'uncanny-automator' ) ),
							String( pagination.currentPage ),
							String( pagination.totalPages ),
							String( pagination.totalItems )
						)
					}
				</span>

				<uo-button
					size="small"
					color="secondary"
					?disabled=${ ! pagination.hasNext || this._isLoadingMore }
					@click=${ this._handleNextPage }
				>
					&rsaquo;
				</uo-button>
			</div>
		`;
	}

	/**
	 * Navigates to the previous page of runs.
	 */
	_handlePrevPage() {
		this._navigateToPage( -1 );
	}

	/**
	 * Navigates to the next page of runs.
	 */
	_handleNextPage() {
		this._navigateToPage( 1 );
	}

	/**
	 * Dispatches a page navigation event for loop runs.
	 *
	 * @param {Number} direction -1 for previous, +1 for next.
	 */
	_navigateToPage( direction ) {
		const pagination = this.miscellaneous?.pagination;
		if ( ! pagination ) {
			return;
		}

		const targetPage = pagination.currentPage + direction;
		if ( targetPage < 1 || targetPage > pagination.totalPages ) {
			return;
		}

		this._isLoadingMore = true;

		// Safety net: reset loading state after 15s if the response never arrives.
		clearTimeout( this._loadingTimeout );
		this._loadingTimeout = setTimeout( () => {
			this._isLoadingMore = false;
		}, 15000 );

		this.requestUpdate();

		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-load-more-runs', {
				bubbles: true,
				composed: true,
				detail: {
					actionId: pagination.actionId,
					page: targetPage,
					perPage: pagination.perPage || UncannyAutomatorBackend.logs.runsPerPage,
				}
			} )
		);
	}

	/**
	 * Generates the HTML template for a run item within the sidebar.
	 *
	 * This template includes a summary that is displayed by default and a list of properties related to the run. The summary is shown as a collapsible detail element.
	 *
	 * @param {Object} run - The data object for the run.
	 * @param {String} run.title - The title of the run.
	 * @param {Array<Object>} [run.summary] - An array of summary items for the run.
	 * @param {String} run.summary[].label - The label of the summary item.
	 * @param {String|TemplateResult} run.summary[].value - The value of the summary item.
	 * @param {Array<Object>} run.properties - An array of properties for the run.
	 * @param {String} run.properties[].type - The type of the property (e.g., 'url', 'email').
	 * @param {String} run.properties[].label - The label of the property.
	 * @param {String|Object} run.properties[].value - The value of the property.
	 * @param {Object} [run.properties[].attributes] - Additional attributes for the property.
	 * @param {Number} index - The index of the run in the list of runs.
	 * @return {TemplateResult} The HTML template for the run item.
	 */
	templateRunItem( run, index ) {
		return html`
			<details
				?open=${ index === 0 /* First open by default */ }
				class="sidebar-runs__run"
			>
				<summary>
					<div class="sidebar-runs__run-title">
						${ run.title }
					</div>
					
					<uo-icon id="angle-down"></uo-icon>
				</summary>

				${
					! isEmpty( run.summary ) ?
					html `
						<div class="sidebar-runs__run-summary">
							<table class="sidebar-runs__run-summary-table">
								<tbody>
									${
										repeat(
											run.summary,
											( item, index ) => index,
											item => {
												return html`
													<tr>
														<td class="item__key">
															${ item.label }
														</td>
														<td class="item__value">
															${ item.value }
														</td>
													</tr>
												`;
											}
										)
									}
								</tbody>
							</table>
						</div>
					` :
					''
				}

				${
					! isEmpty( run.properties ) ?
					html `
						<div class="sidebar-runs__run-properties">
							${
								repeat(
									run.properties,
									( property, index ) => index,
									property => html`
										<uap-log-sidebar-property
											.type="${ property.type }"
											.label="${ property.label }"
											.value="${ property.value }"
											.attributes="${ property.attributes }"
										></uap-log-sidebar-property>
									`
								)
							}
						</div>
					` :
					''
				}
			</details>
		`;
	}

	/**
	 * When there is only a single run, Automator will typically hide the run's summary and attempt to display only the properties, which usually pertains to the fields. However, for some actions, like those executed within loops, it is necessary to always display the summary of each run, regardless of whether there is only one run.
	 *
	 * @type {Boolean} - Whether to always show the run's summary
	 * @default false
	 */
	get runsAlwaysShowSummaries() {
		return isDefined( this.miscellaneous?.runsAlwaysShowSummaries ) ?
			this.miscellaneous.runsAlwaysShowSummaries :
			false;
	}
}
